import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { endSession } from "@/lib/endSession.ts";
import { deleteUserAccount } from "@/lib/user.ts";
import { ArrowUp } from "lucide-react";
import { useState } from "react";

type Props = {
  error: string;
  /** Omitted on the first screen, which has nothing behind it to go back to. */
  back?: () => void;
};

export const PhoneAlreadyTakenPanel = ({ error, back }: Props) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteUserAccount("DELETE");
      // Deleting the only account ends the session; wipe like a logout.
      endSession("/auth");
    } catch (err) {
      console.error("Failed to delete account:", err);
      setDeleteError("Failed to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-red-600">{deleteError || error}</p>
      <div className="flex items-center gap-2">
        {back && (
          <Button
            variant="outline"
            onClick={back}
            data-statsig-name="onboarding-phone-already-taken-back"
          >
            <div className="flex items-center gap-2">
              Back
              <ArrowUp className="w-4 h-4" />
            </div>
          </Button>
        )}
        <Button
          variant="destructive"
          onClick={() => setShowDeleteConfirm(true)}
          data-statsig-name="onboarding-phone-already-taken-delete-account"
        >
          Delete this account
        </Button>
      </div>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this account. You can then sign in with your existing
              account that owns this phone number.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
